import {Component, computed, inject, input, linkedSignal, OnInit, output, signal} from "@angular/core";
import {ApiError, ErrorCodes, Event, EventState, PeerPracticeSession, SessionState} from "@models";
import {zoomInOnEnterAnimation} from "angular-animations";
import {NotificationService, SimMaterialDialogService, SimSessionService, SimUtilityService} from "@services";
import {Translations} from "@translations";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {EnumLabel, EnumLabelPipe} from "src/app/shared/shared-pipes/enum-label.pipe";
import {MatIcon} from "@angular/material/icon";
import {ActionMenuComponent} from "@common/ui/action-menu/action-menu.component";
import {SimTimeService} from "../../../../../../core/services/sim-services/sim-time.service";
import {SessionRunPreviewComponent, SessionRunWrapper} from "@common/components/session/session-run-preview/session-run-preview.component";
import {SessionStatusTagComponent} from "@common/components/session/session-status-tag/session-status-tag.component";
import {SessionStationPreviewComponent} from "@common/components/session/session-station-preview/session-station-preview.component";
import {PeerPracticeSessionCardStationContentComponent} from "@common/components/session/session-card/session-type/peer-practice/content/peer-practice-session-card-station-content.component";
import {Router} from "@angular/router";
import {routes} from "../../../../../../../environments/routes";
import {MatButton} from "@angular/material/button";
import {SimButton} from "@common/ui/button/sim-button.component";
import {MatTooltip} from "@angular/material/tooltip";
import {BusyDirective} from "@common/directives/busy.directive";
import {EventEmitterService} from "@event-emitter";
import {TimeRangeLabelPipe} from "@common/pipes/time/time-range-label.pipe";

@Component({
	selector: "sim-peer-practice-session-card",
	animations: [zoomInOnEnterAnimation({duration: 175})],
	templateUrl: "./peer-practice-session-card.component.html",
	styleUrl: "./peer-practice-session-card.component.scss",
	imports: [
		EnumLabelPipe,
		MatIcon,
		ActionMenuComponent,
		PeerPracticeSessionCardStationContentComponent,
		SessionRunPreviewComponent,
		TranslateModule,
		SessionStatusTagComponent,
		SessionStationPreviewComponent,
		MatButton,
		SimButton,
		MatTooltip,
		BusyDirective,
		TimeRangeLabelPipe,
	],
})
export class PeerPracticeSessionCardComponent implements OnInit {
	// I/O
	public readonly inputSession = input.required<PeerPracticeSession>({alias: "session"});
	public readonly event = input.required<Event>();
	public readonly showActionMenu = input<boolean>(false);
	public readonly onDelete = output<void>();
	public readonly onEdit = output<void>();

	// Template Constants
	protected readonly T = Translations;
	protected readonly EnumLabel = EnumLabel;

	// Injections
	readonly translate = inject(TranslateService);
	readonly #dialogService = inject(SimMaterialDialogService);
	readonly #timeService = inject(SimTimeService);
	readonly #sessionService = inject(SimSessionService);
	readonly #router = inject(Router);
	readonly #eventEmitter = inject(EventEmitterService);
	readonly #notificationService = inject(NotificationService);

	// Properties
	protected readonly session = linkedSignal<PeerPracticeSession>(() => this.inputSession());
	protected readonly runs = computed<SessionRunWrapper[]>(() => this.computeRuns(this.session()));
	protected readonly timeFormat = this.#timeService.timeFormat;
	protected readonly canOpenMcApp = computed<boolean>(() => this.computeCanOpenMcApp(this.session()));
	protected readonly canStartSession = computed<boolean>(() => this.computeCanStartSession(this.session(), this.event()));
	protected readonly isScheduledToday = computed<boolean>(
		() => !!SimUtilityService.toDayJs(this.session().startTime)?.isSame(new Date(), "day"),
	);
	protected busyPromise = signal<Promise<unknown> | undefined>(undefined);
	public canCancelSession = computed<boolean>(
		() => ![SessionState.Scheduled, SessionState.Aborted, SessionState.Finished].includes(this.session().state),
	);

	ngOnInit(): void {
		//Subscribe to session updates
		this.#eventEmitter.onTrainingSessionUpdated.subscribe((updatedSessionEvent) => {
			if (updatedSessionEvent.simSession.id === this.session()?.id) {
				this.session.set(updatedSessionEvent.simSession as PeerPracticeSession);
			}
		});
	}

	protected confirmDelete(): void {
		this.#dialogService
			.openDeleteDialog({
				deleteTitle: this.translate.instant(this.T.SESSION_SELECTION_DELETE_CREATED_SESSION),
				deleteText: this.translate.instant(this.T.SESSION_SELECTION_DELETE_CREATED_SESSION_TXT),
				deleteButtonText: this.translate.instant(this.T.BUTTON_DELETE),
				cancelText: this.translate.instant(this.T.BUTTON_CANCEL),
			})
			.then(({action}) => {
				if (action === "ok") {
					this.onDelete.emit();
				}
			});
	}

	protected openEdit(): Function {
		return () => this.onEdit.emit();
	}

	protected async openMcApp(): Promise<void> {
		await this.#router.navigate([`${routes.routes.masterOfCeremonyApp}/peer-practice/${this.session().id}`]);
	}

	protected startSession(): void {
		const promise = this.#sessionService.startPeerPracticeSession(this.session().id).catch(async (error: ApiError) => {
			if (error.simError?.errorCode === ErrorCodes.RoomsInUseError) {
				await this.#dialogService.openManageBlockingSessionsDialog({sessionId: this.session().id});
			}
		});
		this.busyPromise.set(promise);
	}

	protected cancelSession(): void {
		this.#dialogService
			.openConfirmationDialog({
				title: this.T.CONFIRMATION_DIALOG_ABORT_SESSION_TITLE,
				cancelText: this.T.BUTTON_CANCEL,
				description: this.T.CONFIRMATION_DIALOG_ABORT_SESSION_DESC,
				okText: this.T.BUTTON_OK,
			})
			.then((dialogResult) => {
				if (dialogResult.action === "ok") {
					this.#sessionService.abortSession({sessionId: this.session().id}).then(() => {
						this.#notificationService.notifySuccess(
							this.translate.instant(Translations.CONFIRMATION_DIALOG_ABORT_SESSION_SUCCESS),
						);
					});
				}
			});
	}

	private computeRuns(session: PeerPracticeSession): SessionRunWrapper[] {
		const result: SessionRunWrapper[] = [];
		let counter = new Date(session.startTime);

		for (let i = 0; i < session.schedule.length; i += 2) {
			const run = session.schedule[i];
			const intermission = session.schedule[i + 1];

			if (!run) {
				throw new Error("Run is undefined");
			}

			const startTime = counter;
			const durationMinutes = Math.floor(run.duration / 60_000);
			const intermissionDurationMinutes = intermission?.duration ? Math.floor(intermission.duration / 60_000) : undefined;

			result.push({
				runId: run.id,
				startTime,
				durationMinutes,
				intermissionDurationMinutes,
			});

			counter = new Date(startTime.getTime() + run.duration + intermission?.duration);
		}

		return result;
	}

	private computeCanStartSession(session: PeerPracticeSession, event: Event): boolean {
		return [SessionState.Scheduled].includes(session.state) && event.state === EventState.Active;
	}

	private computeCanOpenMcApp(session: PeerPracticeSession): boolean {
		return [SessionState.Running].includes(session.state);
	}
}
